/**
 * Where to send somebody once they have signed in.
 *
 * Every gated page is behind `proxy.ts`, and when an unauthenticated request
 * arrives NextAuth bounces it to `/login?callbackUrl=<the URL they asked for>`.
 * Without it, anyone following a deep link — a job link in Teams, a bookmark, a link in an alert
 * card — would land on the dashboard with no idea where the thing they clicked
 * had gone. This turns that parameter back into a destination.
 *
 * It is deliberately paranoid, because `callbackUrl` is attacker-supplied: it
 * is a query parameter on a public page, so anybody can hand a colleague a
 * link to our real login screen that redirects somewhere else the moment they
 * sign in. Only a path on this origin survives, and only one worth landing on.
 */

export const DEFAULT_LANDING = "/dashboard";

/**
 * Routes nobody should be returned to after signing in. `/login` and friends
 * would bounce an authenticated user straight back out again (or, at `/login`,
 * simply loop); `/api` is not a page. Matched on a whole path segment so
 * `/apiary` is not mistaken for `/api`.
 */
const NOT_A_DESTINATION = ["/login", "/forgot-password", "/reset-password", "/api"];

function isBlocked(pathname: string): boolean {
  return NOT_A_DESTINATION.some((base) => pathname === base || pathname.startsWith(base + "/"));
}

/** A C0 control character (U+0000 to U+001F) or DEL. */
function hasControlCharacter(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/**
 * @param raw    the `callbackUrl` query parameter, as NextAuth wrote it — an
 *               absolute same-origin URL in practice, but treat it as hostile.
 * @param origin `window.location.origin` (passed in so this stays a pure
 *               function and can be tested in node).
 * @returns a same-origin path safe to assign to `window.location`, or
 *          {@link DEFAULT_LANDING}.
 */
export function safeCallbackUrl(raw: string | null | undefined, origin: string): string {
  if (!raw) return DEFAULT_LANDING;
  const value = raw.trim();
  if (!value) return DEFAULT_LANDING;

  // A newline or a control character in a redirect target is never innocent.
  if (hasControlCharacter(value)) return DEFAULT_LANDING;

  let candidate: string;

  if (value.startsWith("/")) {
    // "//evil.example" is protocol-relative, and browsers treat "/\evil.example"
    // the same way — both leave the origin while looking like a path.
    if (value[1] === "/" || value[1] === "\\") return DEFAULT_LANDING;
    candidate = value;
  } else {
    // Anything else has to prove it belongs to this origin. A scheme we cannot
    // parse, or one with no origin of its own (javascript:, data:), fails here.
    let url: URL;
    try {
      url = new URL(value);
    } catch {
      return DEFAULT_LANDING;
    }
    if (url.origin !== origin) return DEFAULT_LANDING;
    candidate = url.pathname + url.search + url.hash;
  }

  const pathname = candidate.split(/[?#]/)[0];
  if (pathname === "/" || isBlocked(pathname)) return DEFAULT_LANDING;

  return candidate;
}
